import { flexPos } from '@/assets/styles/mixin';
import { FC, ReactNode } from 'react';
import { styled } from 'styled-components';
import BubbleStatus, { BubbleReadStatus } from './BubbleStatus';
import { EMessageStatus, EContentType, IMessage } from '@/store/messageStore';
import { base64ToFile, clone } from '@/utils';
import ProgressCircle from './ProgressCircle';
import useMessageStore from '@/store/messageStore';
import Image from '@cps/MediaComp/Image';
import { v4 as uuidV4 } from 'uuid';
import { useUpload } from '../Upload/useUpload';
import { EFaqKnows, useChat, useFaq, useMessageHistory } from '@/service/useChat';
import { validImage } from '@/utils/valid';
import { errorMsg } from '@/service/serverCode';
import { useCreateChat } from '@/service/useChatConfig';
import { isSimpleMediaSize } from '../Upload/tools';
import { getImageDimensions } from '../Upload/mediaCompression';
import useUserStore from '@/store/userStore';

interface IVideoBubbleProps {
  msgBody: string;
  status: EMessageStatus;
  messageItem: IMessage;
  isMe?: boolean;
  progress?: number;
  isRead: boolean;
  isStopUpload?: boolean;
}

export const VideoBubbleWrap = styled.div<{ $isMe?: boolean }>`
  position: relative;
  ${flexPos('center')}
  justify-content: ${(props) => (props.$isMe ? 'flex-end' : 'flex-start')};

  .video-bubble-content {
    position: relative;
    ${flexPos('center')}
  }

  .icon-video {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 80rem;
    height: 80rem;
  }
`;

const VideoBubble: FC<IVideoBubbleProps> = ({
  msgBody,
  isMe,
  status,
  progress,
  messageItem,
  isRead,
  isStopUpload,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const messageStore = useMessageStore();
  const { doUpload } = useUpload();
  const { msgSend } = useChat();
  const { createSession } = useCreateChat();
  const { seqInfo, getSessionList } = useMessageHistory();
  const { getFaqKnows } = useFaq();
  const userStore = useUserStore();

  const imgUrl = useMemo(() => {
    if (!msgBody || (typeof msgBody === 'object' && !Object.keys(msgBody).length)) {
      return '';
    }

    let srcObj = {
      thumbnail: { key: '', url: '' },
      resource: { key: '', url: '' },
    };
    try {
      const temp = JSON.parse(msgBody);
      srcObj = temp;
    } catch (error) {}
    if (srcObj?.resource?.url) {
      return srcObj;
    }
    if (srcObj.thumbnail?.url) {
      return {
        thumbnail: srcObj.thumbnail,
        resource: { key: '', url: '' },
      };
    }

    const content = base64ToFile(msgBody, `${uuidV4()}.png`);
    const resp = URL.createObjectURL(content);
    return resp;
  }, [msgBody]);

  const getMediaInxById = (uniqueId: string) => {
    const mediaMsgList = messageStore.getMediaMsgList();
    return mediaMsgList.findIndex((item) => item.uniqueId === uniqueId);
  };

  const progressStatus = useMemo(() => {
    if (status === EMessageStatus.success) {
      return 100;
    }
    return progress || 0;
  }, [progress, status]);

  const messageContainerRef = useRef<HTMLElement | null>();
  useEffect(() => {
    messageContainerRef.current = document.getElementById('messageContainer');
  }, []);

  return (
    <VideoBubbleWrap $isMe={isMe}>
      <BubbleReadStatus isRead={!!isMe && isRead} />

      <ProgressCircle
        isMe={!!isMe}
        className="ml-16"
        progress={progressStatus}
        status={status}
        isStopUpload={isStopUpload}
        onClickMask={async () => {
          if (status === EMessageStatus.fail && !messageItem.isLockClickUpload) {
            const cloneMsgItem = clone(messageItem, true);
            messageStore.updateIsLockClickUpload(cloneMsgItem, true);

            const restoredFile = new File(
              [messageItem.fileData?.content] as any,
              messageItem.fileData?.metadata.name as any,
              {
                type: messageItem.fileData?.metadata.type,
                lastModified: Date.now(),
              }
            );

            if (messageItem) {
              const ff = base64ToFile(messageItem.msgBody);
              const thumbnail = await doUpload(ff);
              const imageSize = await getImageDimensions(ff);

              cloneMsgItem.thumbnail = JSON.stringify(thumbnail);
              messageStore.updateMessageThumbnail(cloneMsgItem);

              let src;
              try {
                src = await doUpload(
                  restoredFile,
                  true,
                  (progress) => {
                    const tempMsg = clone(cloneMsgItem, true);
                    tempMsg.status = EMessageStatus.pending;
                    tempMsg.progress = progress;

                    messageStore.updateMessageProgress2(tempMsg);
                  },
                  (uploadId) => {
                    console.log('停止callback...', uploadId);
                    cloneMsgItem.uploadId = uploadId;
                    messageStore.updateMessageUploadId(cloneMsgItem);

                    const tempMsgList = messageStore.getMsgList();
                    const targetMsg = tempMsgList.find((item) => {
                      return item.uniqueId === cloneMsgItem.uniqueId;
                    });
                    console.log('targetMsg...', targetMsg);
                    return targetMsg?.isStopUpload || false;
                  }
                );
              } catch (error) {
                console.log('doupload repeat error', error);
              }

              const isImage = messageItem.msgType === EContentType.image;
              // console.log('原来的缩略图...', messageItem.thumbnail);

              // let tempThumbnail = '';
              // try {
              //   tempThumbnail = JSON.parse(messageItem.thumbnail!);
              // } catch (error) {}

              if (src?.url) {
                const resource = isImage
                  ? {
                      key: src.key,
                      url: src.url,
                    }
                  : {
                      key: '',
                      url: src.url,
                    };

                const doSend = async () => {
                  const resp = await msgSend({
                    msgBody: JSON.stringify({
                      thumbnail: thumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    }),
                    msgSeq: cloneMsgItem.msgSeq,
                    msgType: cloneMsgItem.msgType,
                    clientMsgID: cloneMsgItem.clientMsgID,
                    sendTime: cloneMsgItem.sendTime,
                  });

                  if (resp.success) {
                    const cloneMsgpack = clone(cloneMsgItem, true);
                    cloneMsgpack.msgBody = JSON.stringify({
                      thumbnail: thumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    });
                    messageStore.setLatestSeq(resp.data?.msgReq || 0);
                    cloneMsgpack.status = EMessageStatus.success;
                    cloneMsgpack.msgSeq = resp.data?.msgReq!;
                    cloneMsgpack.sessionId = userStore.getSessionConfig()?.sessionId;

                    // messageStore.updateMessageItem(msgPack);
                    // await localforage.setItem(sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg', msgList);

                    messageStore.updateMessageItem(cloneMsgpack);
                  } else if (
                    [
                      errorMsg.sessionError.code,
                      errorMsg.sessionFail.code,
                      errorMsg.sessionTimeout.code,
                    ].includes(resp.code as any)
                  ) {
                    const resp = await createSession();

                    const sessionList = await getSessionList();
                    seqInfo.current.sessionList = sessionList;

                    if (resp.code === 0) {
                      await doSend();
                      await getFaqKnows(EFaqKnows.knows, '');
                    }
                  }
                };

                doSend();
              }
            }

            messageStore.updateIsLockClickUpload(cloneMsgItem, false);
          }

          const _file = messageItem.file || messageItem?.fileData?.content

          const isSimple = isSimpleMediaSize(_file?.size || 0);

          if (!isSimple && status === EMessageStatus.pending) {
            const cloneMsgItem = clone(messageItem, true);
            let imageSize = {
              width: 100,
              height: 100,
            };
            if (cloneMsgItem?.file) {
              const ff = base64ToFile(cloneMsgItem.msgBody);
              imageSize = await getImageDimensions(ff);
            }

            try {
              cloneMsgItem.isStopUpload = !cloneMsgItem.isStopUpload;
              messageStore.updateMessageItem(cloneMsgItem);
            } catch (error) {}

            let restoredFile;
            if (messageItem.fileData) {
              restoredFile = new File(
                [messageItem.fileData?.content] as any,
                messageItem.fileData?.metadata.name as any,
                {
                  type: messageItem.fileData?.metadata.type,
                  lastModified: Date.now(),
                }
              );
            } else {
              restoredFile = _file;
            }

            if (restoredFile && messageItem.isStopUpload) {
              let src;
              try {
                src = await doUpload(
                  restoredFile,
                  true,
                  (progress) => {
                    const tempMsg = clone(cloneMsgItem, true);
                    tempMsg.progress = progress;
                    messageStore.updateMessageProgress(tempMsg);
                  },
                  () => {
                    const tempMsgList = messageStore.getMsgList();
                    const targetMsg = tempMsgList.find((item) => {
                      return item.uniqueId === cloneMsgItem.uniqueId;
                    });
                    console.log('targetMsg...', targetMsg);
                    return targetMsg?.isStopUpload || false;
                  },
                  'repeat',
                  messageItem?.uploadId
                );
              } catch (error) {
                console.log('doupload repeat error', error);
              }

              if (src?.url) {
                const isImage = validImage(restoredFile.type);

                let tempThumbnail = '';
                try {
                  tempThumbnail = JSON.parse(messageItem.thumbnail!);
                } catch (error) {}

                const resource = isImage
                  ? {
                      key: src.key,
                      url: src.url,
                    }
                  : {
                      key: '',
                      url: src.url,
                    };

                const doSend = async () => {
                  const resp = await msgSend({
                    msgBody: JSON.stringify({
                      thumbnail: tempThumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    }),
                    msgSeq: cloneMsgItem.msgSeq,
                    msgType: cloneMsgItem.msgType,
                    clientMsgID: cloneMsgItem.clientMsgID,
                    sendTime: cloneMsgItem.sendTime,
                  });

                  if (resp.success) {
                    const cloneMsgpack = clone(cloneMsgItem, true);
                    cloneMsgpack.msgBody = JSON.stringify({
                      thumbnail: tempThumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    });
                    messageStore.setLatestSeq(resp.data?.msgReq || 0);
                    cloneMsgpack.status = EMessageStatus.success;
                    cloneMsgpack.msgSeq = resp.data?.msgReq!;
                    // messageStore.updateMessageItem(msgPack);
                    // await localforage.setItem(sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg', msgList);

                    messageStore.updateMessageItem(cloneMsgpack);
                  } else if (
                    [
                      errorMsg.sessionError.code,
                      errorMsg.sessionFail.code,
                      errorMsg.sessionTimeout.code,
                    ].includes(resp.code as any)
                  ) {
                    const resp = await createSession();

                    const sessionList = await getSessionList();
                    seqInfo.current.sessionList = sessionList;

                    if (resp.code === 0) {
                      await doSend();

                      await getFaqKnows(EFaqKnows.knows, '');
                    }
                  }
                };

                doSend();
              }
            }
          }
        }}
        render={(isLoaded) => (
          <div
            className="video-bubble-content"
            onClick={() => {
              const inx = getMediaInxById(messageItem.uniqueId);
              messageStore.setActivePreviewMediaInx(inx);
              messageStore.setShowPreviewMedia(true);
            }}
          >
            {isLoaded && status === EMessageStatus.success && (
              <img src={require('@img/chat/icon-video.svg')} alt="" className="icon-video" />
            )}
            {/* <img src={imgUrl} alt="" className="max-w-320 min-w-180 rounded-32" /> */}
            <Image
              onLoad={() => {
                if (messageItem.isInitMsg) {
                  setTimeout(() => {
                    const target = messageContainerRef.current;
                    target &&
                      target.scrollTo({
                        top: target.scrollHeight,
                        behavior: 'smooth',
                      });
                  }, 199);
                }
              }}
              src={imgUrl}
              className="max-w-320 min-w-180 rounded-32"
            />
          </div>
        )}
      ></ProgressCircle>
    </VideoBubbleWrap>
  );
};

export default VideoBubble;
